import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, SparklesIcon } from 'lucide-react';
export function Hero() {
  return <section className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-screen flex items-center" data-id="element-129">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-20" data-id="element-130">
        <motion.div animate={{
        backgroundPosition: ['0% 0%', '100% 100%']
      }} transition={{
        duration: 20,
        repeat: Infinity,
        repeatType: 'reverse',
        ease: 'linear'
      }} className="absolute inset-0" style={{
        backgroundImage: `linear-gradient(to right, rgba(251, 146, 60, 0.1) 1px, transparent 1px),
                             linear-gradient(to bottom, rgba(251, 146, 60, 0.1) 1px, transparent 1px)`,
        backgroundSize: '64px 64px'
      }} data-id="element-131" />
      </div>

      {/* Glowing Orbs */}
      <motion.div animate={{
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.5, 0.3]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-1/4 right-1/4 w-96 h-96 bg-orange-500/30 rounded-full blur-3xl" data-id="element-132" />
      <motion.div animate={{
      scale: [1, 1.3, 1],
      opacity: [0.2, 0.4, 0.2]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" data-id="element-133" />

      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-20 px-6 py-6 lg:px-8" data-id="element-134">
        <div className="mx-auto max-w-7xl flex items-center justify-between" data-id="element-135">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} className="flex items-center gap-3" data-id="element-136">
            <div className="relative" data-id="element-137">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl blur-lg opacity-50" data-id="element-138" />
              <div className="relative w-10 h-10 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center" data-id="element-139">
                <span className="text-white font-bold text-xl" data-id="element-140">M</span>
              </div>
            </div>
            <span className="text-xl font-bold text-white" data-id="element-141">
              Master Merchant
            </span>
          </motion.div>
          <motion.button initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} className="px-6 py-2.5 bg-white text-slate-900 rounded-full font-semibold hover:bg-orange-50 transition-colors" data-id="element-142">
            Get Started
          </motion.button>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 py-20 lg:px-8 w-full" data-id="element-143">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-144">
          {/* Left Column */}
          <div data-id="element-145">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 backdrop-blur-sm rounded-full border border-orange-500/20 mb-8" data-id="element-146">
              <SparklesIcon className="w-4 h-4 text-orange-400" data-id="element-147" />
              <span className="text-sm font-medium text-orange-300" data-id="element-148">
                Nonprofit-owned & operated
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6" data-id="element-149">
              Accept donations in{' '}
              <span className="relative inline-block" data-id="element-150">
                <span className="relative z-10 bg-gradient-to-r from-orange-400 via-pink-400 to-orange-400 bg-clip-text text-transparent" data-id="element-151">
                  30 minutes
                </span>
                <motion.span initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 0.8,
                delay: 0.8
              }} className="absolute bottom-2 left-0 right-0 h-3 bg-gradient-to-r from-orange-500/30 to-pink-500/30 -z-0" data-id="element-152" />
              </span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="text-xl text-slate-300 mb-10 leading-relaxed" data-id="element-153">
              The only nonprofit payment processor built by nonprofits. No setup
              fees, no hidden costs, no BS. Just instant donation processing.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.5
          }} className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-154">
              <button className="group relative px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-semibold overflow-hidden" data-id="element-155">
                <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-156">
                  Start Free Trial
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-157" />
                </span>
                <motion.div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-orange-500" initial={{
                x: '100%'
              }} whileHover={{
                x: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-158" />
              </button>
              <button className="px-8 py-4 bg-white/5 backdrop-blur-sm text-white rounded-full font-semibold hover:bg-white/10 transition-colors border border-white/10" data-id="element-159">
                See Pricing
              </button>
            </motion.div>

            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} className="flex items-center gap-8 text-sm text-slate-400" data-id="element-160">
              <div className="flex items-center gap-2" data-id="element-161">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-162" />
                <span data-id="element-163">Live in 30 min</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-164">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-165" />
                <span data-id="element-166">No credit card</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-167">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-168" />
                <span data-id="element-169">Cancel anytime</span>
              </div>
            </motion.div>
          </div>

          {/* Right Column - 3D Card Stack */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="relative h-[600px] hidden lg:block" data-id="element-170">
            {/* Card 1 - Front */}
            <motion.div animate={{
            y: [0, -10, 0],
            rotateY: [0, 5, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-0 right-0 w-80 bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 shadow-2xl border border-white/10" style={{
            transform: 'perspective(1000px) rotateY(-10deg)'
          }} data-id="element-171">
              <div className="flex justify-between items-start mb-12" data-id="element-172">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-pink-500 rounded-2xl flex items-center justify-center" data-id="element-173">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-174">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-175" />
                  </svg>
                </div>
                <div className="text-right" data-id="element-176">
                  <div className="text-slate-400 text-sm mb-1" data-id="element-177">
                    Donation Card
                  </div>
                  <div className="text-white font-semibold" data-id="element-178">
                    Master Merchant
                  </div>
                </div>
              </div>
              <div className="space-y-4 mb-8" data-id="element-179">
                <div className="h-12 bg-gradient-to-r from-orange-500/20 to-pink-500/20 rounded-xl backdrop-blur-sm" data-id="element-180" />
                <div className="flex gap-4" data-id="element-181">
                  <div className="flex-1 h-12 bg-white/5 rounded-xl" data-id="element-182" />
                  <div className="flex-1 h-12 bg-white/5 rounded-xl" data-id="element-183" />
                </div>
              </div>
              <div className="flex justify-between items-center" data-id="element-184">
                <div className="text-slate-400 text-sm" data-id="element-185">Instant Processing</div>
                <div className="flex gap-2" data-id="element-186">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg" data-id="element-187" />
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg" data-id="element-188" />
                </div>
              </div>
            </motion.div>

            {/* Card 2 - Behind */}
            <motion.div animate={{
            y: [0, -5, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5
          }} className="absolute top-12 right-12 w-80 bg-gradient-to-br from-orange-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-8 border border-white/10" style={{
            transform: 'perspective(1000px) rotateY(-10deg) translateZ(-50px)'
          }} data-id="element-189">
              <div className="h-full flex items-center justify-center" data-id="element-190">
                <div className="text-center" data-id="element-191">
                  <div className="text-6xl font-bold text-white mb-2" data-id="element-192">
                    $500M+
                  </div>
                  <div className="text-slate-300" data-id="element-193">Processed</div>
                </div>
              </div>
            </motion.div>

            {/* Floating Stats */}
            <motion.div initial={{
            opacity: 0,
            x: -40
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 1
          }} className="absolute left-0 top-32 bg-slate-800/90 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10" data-id="element-194">
              <div className="text-4xl font-bold bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent mb-1" data-id="element-195">
                30min
              </div>
              <div className="text-sm text-slate-400" data-id="element-196">Setup Time</div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 40
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 1.2
          }} className="absolute right-0 bottom-32 bg-slate-800/90 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10" data-id="element-197">
              <div className="text-4xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-1" data-id="element-198">
                99.9%
              </div>
              <div className="text-sm text-slate-400" data-id="element-199">Uptime</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}